import { useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Tag } from "lucide-react";
import { toast } from "sonner";
import { SoleCardForm, SoleCodeGroup } from "@/components/SoleCardParts";

const empty = { sole_code: "", sole_color: "", supplier: "", note: "" };

export default function SoleCards({ onChanged }) {
  const { soleCards, products, suppliers, refreshSoleCards, refreshProducts, refreshSuppliers } = useData();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const codes = useMemo(() => [...new Set(soleCards.map((c) => c.sole_code))], [soleCards]);
  const modelsByCard = useMemo(() => {
    const m = {};
    products.forEach((p) => { const k = `${p.sole_code}||${p.sole_color || ""}`; (m[k] = m[k] || new Set()).add(p.model_code); });
    return m;
  }, [products]);
  const modelsOf = (c) => [...(modelsByCard[`${c.sole_code}||${c.sole_color || ""}`] || [])];

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({ sole_code: c.sole_code, sole_color: c.sole_color, supplier: c.supplier, note: c.note });
    setOpen(true);
  };

  const [lockCode, setLockCode] = useState(false);
  const startAddColor = (code) => {
    setEditingId(null); setLockCode(true);
    setForm({ ...empty, sole_code: code, supplier: soleCards.find((c) => c.sole_code === code)?.supplier || "" });
    setOpen(true);
  };

  const refreshAll = async () => { await Promise.all([refreshSoleCards(), refreshProducts(), refreshSuppliers()]); onChanged?.(); };

  const submit = async () => {
    if (!form.sole_code.trim()) { toast.error("Taban kodu zorunlu"); return; }
    if (!form.sole_color.trim()) { toast.error("Taban rengi zorunlu"); return; }
    if (!form.supplier) { toast.error("Taban firması seçin"); return; }
    setSubmitting(true);
    try {
      if (editingId) await api.put(`/soles/cards/${editingId}`, form);
      else await api.post("/soles/cards", form);
      toast.success(editingId ? "Taban kartı güncellendi" : "Taban kartı oluşturuldu");
      setOpen(false); setForm(empty); setEditingId(null); setLockCode(false);
      refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSubmitting(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Taban kartı silinsin mi?")) return;
    try { await api.delete(`/soles/cards/${id}`); toast.success("Silindi"); refreshAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <section className="rounded-lg border border-[#DCCFB8] bg-[#FFFBF4] p-5 mb-6" data-testid="sole-cards-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold uppercase tracking-tight flex items-center gap-2">
          <Tag className="w-5 h-5 text-[#B45309]" /> Taban Stok Kartları
        </h2>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); setLockCode(false); } }}>
          <DialogTrigger asChild>
            <Button data-testid="sole-card-new-btn" className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-10 px-4">
              <Plus className="w-4 h-4 mr-2" /> Yeni Taban Kartı
            </Button>
          </DialogTrigger>
          <SoleCardForm form={form} setForm={setForm} editingId={editingId} lockCode={lockCode}
            suppliers={suppliers} submitting={submitting} onSubmit={submit} />
        </Dialog>
      </div>
      {soleCards.length === 0 && <p className="text-sm text-[#8C7B68]" data-testid="sole-cards-empty">Henüz taban kartı yok.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sole-cards-table">
        {codes.map((code) => (
          <SoleCodeGroup key={code} code={code} colors={soleCards.filter((c) => c.sole_code === code)} modelsOf={modelsOf}
            onAddColor={startAddColor} onEdit={startEdit} onRemove={remove} />
        ))}
      </div>
    </section>
  );
}
